<template>
  <!-- 首页与全部记录页共用的任务行：variant="recent" 首页行 / "history" 记录页卡片行 -->
  <div v-if="variant === 'recent'" class="record-row">
    <div class="record-file">
      <div class="case-icon">
        <FileThumb :name="task.type + '.docx'" />
      </div>
      <div class="case-main">
        <div class="case-name">{{ task.name }}</div>
        <!-- · 共 {{ totalFiles(task) }} 份案件材料 -->
        <div class="case-meta">{{ task.type }} </div>
        <div v-if="task.status === 'missing'" class="case-note">
          {{ task.progress }} · 点击“补充材料”查看需补充内容
        </div>
      </div>
    </div>
    <span class="status" :class="status.cls">
      <span v-if="status.running" class="running-line">
        <span class="spinner"></span>{{ status.text }}
      </span>
      <template v-else>{{ status.text }}</template>
    </span>
    <div class="record-time">{{ task.time }}</div>
    <div class="record-actions">
      <button
        v-for="a in actions"
        :key="a.label"
        class="btn sm record-action"
        :class="{ danger: a.danger }"
        :disabled="a.disabled"
        @click="a.event && $emit(a.event, task)"
      >
        {{ a.label }}
      </button>
    </div>
  </div>

  <article v-else class="history-record">
    <div class="history-record-icon">
      <FileThumb :name="task.type + '.docx'" />
    </div>
    <div class="history-record-main">
      <div class="history-record-name">{{ task.name }}</div>
      <!-- · 共 {{ totalFiles(task) }} 份案件材料 -->
      <div class="history-record-meta">{{ task.type }} </div>
      <div v-if="task.status === 'missing'" class="history-record-note">
        {{ task.progress }} · 点击“补充材料”查看需补充内容
      </div>
    </div>
    <span class="status" :class="status.cls">
      <span v-if="status.running" class="running-line">
        <span class="spinner"></span>{{ status.text }}
      </span>
      <template v-else>{{ status.text }}</template>
    </span>
    <div class="history-record-time">{{ task.time }}</div>
    <div class="history-record-actions">
      <button
        v-for="a in actions"
        :key="a.label"
        class="btn sm"
        :class="{ danger: a.danger }"
        :disabled="a.disabled"
        @click="a.event && $emit(a.event, task)"
      >
        {{ a.label }}
      </button>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import { statusInfo } from "../store";
import FileThumb from "./FileThumb.vue";

const props = defineProps({
  task: { type: Object, required: true },
  variant: { type: String, default: "recent" }, // recent | history
});
defineEmits(["view", "download", "delete", "missing"]);

const status = computed(() => statusInfo(props.task) || {});
// 行操作：无论状态如何 4 个按钮恒展示，仅按状态切换可用 / 禁用——
// 失败态禁查看；仅完成态可下载；仅待补充态可补材料（missing 事件弹缺失资料弹窗，
// 弹窗内再进补充上传）；解析 / 生成中过程态不可删除。
// event 为 emit 名（disabled 按钮 click 不触发，event 只在可用时生效）
const actions = computed(() => {
  const s = props.task.status;
  return [
    { label: "查看", event: "view", disabled: s === "failed" },
    { label: "下载", event: "download", disabled: s !== "done" },
    { label: "补充材料", event: "missing", disabled: s !== "missing" },
    {
      label: "删除",
      event: "delete",
      danger: true,
      disabled: s === "parsing" || s === "generating",
    },
  ];
});
</script>

<style scoped>
/* 组件自带用到的变量（页面根亦有定义，此处保证独立使用时也可用） */
.record-row,
.history-record {
  --brand: #1768ff;
  --line-2: #f0f3f7;
}

.btn {
  height: 32px;
  border: 1px solid #dce4f0;
  border-radius: 8px;
  background: #fff;
  color: #31435f;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 650;
  font-size: 12px;
  cursor: pointer;
  transition: 0.16s;
}
.btn:hover { background: #f8fbff; border-color: #c9d6e8; }
.btn:disabled {
  background: #f4f6f9 !important;
  border-color: #e1e6ee !important;
  color: #a1adbd !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}

/* ---------- 首页行 ---------- */
.record-row {
  min-height: 96px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 138px 330px;
  gap: 16px;
  align-items: center;
  padding: 17px 18px;
  border-bottom: 1px solid var(--line-2);
  transition: 0.16s;
}
.record-row:last-child { border-bottom: 0; }
.record-row:hover { background: #fbfdff; }
.record-file { display: flex; align-items: center; gap: 14px; min-width: 0; }
.case-icon { width: 40px; height: 44px; display: grid; place-items: center; flex: none; }
.case-main { min-width: 0; }
.case-name { font-size: 14px; font-weight: 760; color: #273a55; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.case-meta { margin-top: 7px; color: #7f8da1; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.case-note { margin-top: 7px; font-size: 11px; color: #a46a10; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.record-time { font-size: 12px; color: #74839a; white-space: nowrap; }
.record-actions { justify-self: end; display: flex; align-items: center; gap: 7px; }
.record-action { min-width: 50px; }

/* ---------- 记录页卡片行 ---------- */
.history-record {
  --line-2: #f0f3f7;
  min-height: 92px;
  padding: 16px;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 118px 154px 332px;
  column-gap: 14px;
  align-items: center;
  border: 1px solid #e1e8f2;
  border-radius: 13px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(31, 57, 105, 0.025);
}
.history-record:hover { border-color: #c8d8ef; box-shadow: 0 7px 20px rgba(31, 57, 105, 0.055); }
.history-record-icon { width: 40px; height: 44px; display: grid; place-items: center; }
.history-record-main { min-width: 0; }
.history-record-name { font-size: 14px; font-weight: 780; color: #263a57; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.history-record-meta { margin-top: 6px; font-size: 12px; color: #8390a3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.history-record-note { margin-top: 4px; font-size: 11px; color: #a46a10; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.history-record-time { font-size: 12px; color: #738197; white-space: nowrap; }
.history-record-actions { justify-self: end; display: flex; gap: 7px; }
/* 记录页删除按钮标红（类名不用 delete-btn：全局 enterprise-surface.css 有 !important 覆盖） */
.history-record .btn.danger {
  color: #e34d59;
  border-color: #ffccc7; /* antd red-2 */
}
.history-record .btn.danger:hover {
  background: #fff1f0; /* antd red-1 */
  border-color: #ffa39e; /* antd red-3 */
}

/* ---------- 状态胶囊 ---------- */
.status {
  height: 25px;
  width: max-content;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 750;
}
.status.done { background: #eaf8f2; color: #16825e; }
.status.parsing, .status.generating { background: #eef4ff; color: #1768ff; }
.status.missing { background: #fff5e7; color: #ad6910; }
.status.failed { background: #fff0f2; color: #c73d4b; }
.status.unknown { background: #eef1f5; color: #5b6b80; }
.running-line { display: flex; align-items: center; gap: 7px; }
.spinner { width: 12px; height: 12px; border: 2px solid #cfe0ff; border-top-color: #1768ff; border-radius: 50%; animation: jp-spin 0.8s linear infinite; }
@keyframes jp-spin { to { transform: rotate(360deg); } }

@media (max-width: 1320px) {
  .record-row { grid-template-columns: minmax(0, 1fr) 110px 125px 300px; gap: 10px; }
  .record-actions { gap: 5px; }
  .record-action { padding: 0 8px; }
  .history-record { grid-template-columns: 42px minmax(0, 1fr) 108px 138px 302px; column-gap: 8px; }
}
</style>
